import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import { Website, WebsiteCreate, WebsiteUpdate } from '../types';
import { X } from 'lucide-react';

interface WebsiteFormProps {
  website?: Website | null;
  onClose: () => void;
  onSuccess: () => void;
}

const WebsiteForm: React.FC<WebsiteFormProps> = ({ website, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    website_id: '',
    name: '',
    domain: '',
    description: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domainError, setDomainError] = useState('');
  const [validatingDomain, setValidatingDomain] = useState(false);

  // Domain validation function
  const validateDomain = async (domain: string): Promise<boolean> => {
    if (!domain.trim()) return false;
    
    setValidatingDomain(true);
    setDomainError('');
    
    try {
      // Clean the domain - remove https:// if present
      let cleanDomain = domain.trim().toLowerCase();
      if (cleanDomain.startsWith('https://')) {
        cleanDomain = cleanDomain.replace('https://', '');
      }
      if (cleanDomain.startsWith('http://')) {
        cleanDomain = cleanDomain.replace('http://', '');
      }
      
      // Remove trailing slash
      cleanDomain = cleanDomain.replace(/\/$/, '');
      
      // Test the domain with HTTPS
      const testUrl = `https://${cleanDomain}`;
      
      // Use a timeout to avoid hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      await fetch(testUrl, {
        method: 'GET',
        mode: 'no-cors', // This allows us to test without CORS issues
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // If we get here without an error, the domain is accessible
      return true;
    } catch (error) {
      console.error('Domain validation error:', error);
      setDomainError('Domain is not accessible via HTTPS. Please check the domain and try again.');
      return false;
    } finally {
      setValidatingDomain(false);
    }
  };

  // Website ID generator
  const generateWebsiteId = () => {
    const adjectives = [
      'amazing', 'brilliant', 'creative', 'dynamic', 'elegant', 'fantastic', 'gorgeous',
      'innovative', 'jubilant', 'kinetic', 'luminous', 'magnificent', 'nimble', 'outstanding',
      'powerful', 'quality', 'radiant', 'stellar', 'tremendous', 'unique', 'vibrant',
      'wonderful', 'excellent', 'youthful', 'zealous', 'bright', 'clever', 'daring',
      'energetic', 'friendly', 'gentle', 'happy', 'inspiring', 'joyful', 'kind',
      'lively', 'modern', 'natural', 'optimistic', 'peaceful', 'quick', 'reliable'
    ];

    const nouns = [
      'website', 'portal', 'platform', 'hub', 'center', 'space', 'zone', 'place',
      'corner', 'spot', 'site', 'page', 'blog', 'forum', 'network', 'community',
      'studio', 'workshop', 'lab', 'office', 'base', 'headquarters', 'station',
      'outpost', 'depot', 'store', 'shop', 'market', 'boutique', 'gallery',
      'museum', 'library', 'archive', 'collection', 'showcase', 'display',
      'exhibition', 'presentation', 'show', 'event', 'meeting', 'gathering'
    ];

    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomNumber = Math.floor(Math.random() * 999) + 1;

    const websiteId = `${randomAdjective}-${randomNoun}-${randomNumber}`;
    
    setFormData(prev => ({
      ...prev,
      website_id: websiteId
    }));
  };

  useEffect(() => {
    if (website) {
      setFormData({
        website_id: website.website_id,
        name: website.name,
        domain: website.domain,
        description: website.description || '',
        is_active: website.is_active,
      });
    }
  }, [website]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setDomainError('');

    try {
      // Validate domain before submitting
      if (formData.domain.trim()) {
        const isDomainValid = await validateDomain(formData.domain);
        if (!isDomainValid) {
          setLoading(false);
          return;
        }
      }
      if (website) {
        // Update existing website
        const updateData: WebsiteUpdate = {
          name: formData.name,
          domain: formData.domain,
          description: formData.description,
          is_active: formData.is_active,
        };
        await apiService.updateWebsite(website.id, updateData);
      } else {
        // Create new website
        const createData: WebsiteCreate = {
          website_id: formData.website_id,
          name: formData.name,
          domain: formData.domain,
          description: formData.description,
        };
        await apiService.createWebsite(createData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save website');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Special handling for website_id to ensure lowercase and replace spaces with hyphens
    let processedValue = value;
    if (name === 'website_id') {
      processedValue = value.toLowerCase().replace(/\s+/g, '-');
    }
    
    // Special handling for domain - clean the URL and validate
    if (name === 'domain') {
      // Clean the domain input
      let cleanDomain = value.trim().toLowerCase();
      if (cleanDomain.startsWith('https://')) {
        cleanDomain = cleanDomain.replace('https://', '');
      }
      if (cleanDomain.startsWith('http://')) {
        cleanDomain = cleanDomain.replace('http://', '');
      }
      // Remove trailing slash
      cleanDomain = cleanDomain.replace(/\/$/, '');
      
      processedValue = cleanDomain;
      
      // Clear previous domain error when user starts typing
      if (domainError) {
        setDomainError('');
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : processedValue,
    }));
  };

  // Debounced domain validation
  const handleDomainBlur = async () => {
    if (formData.domain.trim()) {
      await validateDomain(formData.domain);
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 100000 }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
      >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {website ? t('websites.editWebsite') : t('websites.addNewWebsite')}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6">

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!website && (
            <div>
              <label htmlFor="website_id" className="block text-sm font-medium text-gray-700">
                {t('websites.websiteId')}
              </label>
              <div className="mt-1 flex space-x-2">
                <input
                  type="text"
                  name="website_id"
                  id="website_id"
                  required
                  value={formData.website_id}
                  onChange={handleChange}
                  className="flex-1 appearance-none rounded-md relative block px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t('websites.websiteIdPlaceholder')}
                />
                <button
                  type="button"
                  onClick={generateWebsiteId}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  title={t('websites.generateId')}
                >
                  {t('websites.generate')}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t('websites.websiteIdHelp')}
              </p>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              {t('websites.websiteName')}
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="name"
                id="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('websites.websiteNamePlaceholder')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="domain" className="block text-sm font-medium text-gray-700">
              {t('websites.domain')}
            </label>
            <div className="mt-1">
              <div className="relative">
                <input
                  type="text"
                  name="domain"
                  id="domain"
                  required
                  value={formData.domain}
                  onChange={handleChange}
                  onBlur={handleDomainBlur}
                  className={`appearance-none rounded-md relative block w-full px-3 py-2 border ${
                    domainError 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                  } placeholder-gray-500 text-gray-900 focus:outline-none sm:text-sm`}
                  placeholder={t('websites.domainPlaceholder')}
                />
                {validatingDomain && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  </div>
                )}
              </div>
              {domainError && (
                <p className="mt-1 text-sm text-red-600">{domainError}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Only HTTPS domains are allowed. The domain will be tested for accessibility.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              {t('websites.description')}
            </label>
            <div className="mt-1">
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('websites.descriptionPlaceholder')}
              />
            </div>
          </div>


          {website && (
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                {t('common.active')}
              </label>
            </div>
          )}

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('websites.saving') : website ? t('common.update') : t('common.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WebsiteForm;
